<div
  class="tags py-2 px-4"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppablePredicate]="labelDropPredicate"
  (appDroppableDrop)="dropLabels($event)"
>
  <div class="tags-scroll">
    <div
      class="tags-content"
      [appExpandableList]="expanded()"
      [collapsedRows]="2"
      [reserveEnd]="56"
      (expandableMorePosition)="morePosition = $event"
      (expandableOverflowChange)="hasOverflow = $event"
    >
      @if (!withPlaceholder || tags().length > 0) {
        <!-- stitch-app-shell Knowledge box: a small type icon leads the row; its tooltip names it. -->
        <span class="knowledge-row-icon" role="img" aria-label="Tags" title="Tags">
          <app-icon name="sapphire-tags" [size]="16" />
        </span>
      }

      @if (withPlaceholder && tags().length === 0) {
        <div class="tags-placeholder">No tags were added to that item</div>
      }

      <!-- Shared pages: sort/filter stays visible but non-interactive (sorting drops markers — SBUX-539).
           Ahead of the add: both float right, so the + lands to the filter's left. -->
      <app-context-menu-state-indicator
        class="tags-icon no-width print:!hidden"
        matTooltip="Sort & filter"
        [class.pointer-events-none]="isShared"
        svgIcon="filters"
        [appTagsManageContextMenu]="false"
        [appTagsManageContextMenuStateKey]="stateKey"
        [appTagsManageContextMenuHubMode]="isHubMode"
        [appTagsManageContextMenuSuggestions]="suggestionsAllowed"
        (appTagsManageContextMenuStateChange)="setState($event)"
      />

      @if (withControls && !isShared) {
        <!-- [NAV-021] 'Manage my tags' icon removed per task; uncomment (with #manageForm below) to re-enable
        <app-icon
          class="tags-icon print:!hidden"
          name="manage"
          stchPopover
          [stchPopoverArrow]="true"
          [stchPopoverContent]="manageForm"
          stchPopoverTrigger="click"
          stchPopoverPlacement="right"
          [stchPopoverShowUntil]="hidePopover"
        />
        -->

        <!-- Add control is dropped on shared pages; only item-info pages can add tags. -->
        @if (tags().length === 0 && !loading()) {
          <!-- stitch-app-shell Knowledge box: an empty row keeps its dashed "+ Add tags" showing (not while the
               skeleton chips stand in for tags that are still loading). -->
          <button
            type="button"
            class="knowledge-add-pill print:!hidden"
            stchPopover
            [stchPopoverArrow]="true"
            [stchPopoverContent]="addForm"
            stchPopoverTrigger="click"
            stchPopoverPlacement="right"
            [stchPopoverShowUntil]="hidePopover"
          >
            <app-icon name="sapphire-plus" [size]="12" />
            Add tags
          </button>
        } @else {
          <!-- stitch-app-shell Knowledge box AddIcon: the dashed-circle plus, on the filter icon's hover plate. -->
          <button
            type="button"
            class="tags-icon no-width knowledge-add-icon print:!hidden"
            aria-label="Add tags"
            matTooltip="Add tags"
            stchPopover
            [stchPopoverArrow]="true"
            [stchPopoverContent]="addForm"
            stchPopoverTrigger="click"
            stchPopoverPlacement="right"
            [stchPopoverShowUntil]="hidePopover"
          >
            <span class="knowledge-add-icon-plate"><app-icon name="sapphire-plus-circle-dashed" [size]="22" /></span>
          </button>
        }
      }

      <!-- SBUX-1110: skeleton chips instead of an empty row while a saved item's tags load. The skin draws them
           as the Knowledge box's 20px round pills. -->
      @if (loading() && !withPlaceholder && !isShared) {
        <span data-testid="tags-skeleton">
          <stch-skeleton-block class="mr-2 inline-block h-5 w-20 rounded-full align-middle bg-blue-200/60" />
          <stch-skeleton-block class="mr-2 inline-block h-5 w-28 rounded-full align-middle bg-blue-200/60" />
          <stch-skeleton-block class="inline-block h-5 w-16 rounded-full align-middle bg-blue-200/60" />
        </span>
      }

      <!-- The knots mirror carries the reasoning. (`#1146` round 4; `#1174` review F1 for the disabled state.) -->
      @for (tag of displayedTags(); track tag.name) {
        <!-- A Detached tag fades and shrinks out before it goes (Johanna's prototype `.tag.fading`, Pavel 2026-11-03). -->
        <button
          type="button"
          class="tags-activator mr-2"
          [attr.data-chip]="'tag:' + tag.name"
          [class.tag-leaving]="leaving().has('tag:' + tag.name)"
          [attr.inert]="leaving().has('tag:' + tag.name) ? '' : null"
          [disabled]="isShared"
          [attr.aria-label]="'#' + (tag?.name || 'tag')"
          [attr.aria-pressed]="selectedTags() | includesBy : tag.name : 'name'"
          (click)="handleClick(tag, $event)"
        >
          <app-tag
            [tag]="tag"
            [withControls]="withControls && !isShared"
            [withDetach]="!isShared"
            [dragEnabled]="!isShared"
            [selected]="selectedTags() | includesBy : tag.name : 'name'"
            [tenantId]="owningTenantId"
            (delete)="handleDelete(tag)"
            (pin)="handlePin(tag)"
          />
        </button>
      }

      <!-- Suggested tags (Johanna's design, option A): inline after the item's own tags, a grey ⊕ before the name, no
           count and no dismiss (Pavel, 2026-11-03). A click makes it a real tag; the hover hint stays. -->
      @for (suggestion of shownSuggestions(); track suggestion.key) {
        <button
          type="button"
          class="tag-suggestion mr-2 print:!hidden"
          [attr.data-chip]="'suggested:' + suggestion.key"
          [class.tag-leaving]="leaving().has('suggested:' + suggestion.key)"
          [attr.inert]="leaving().has('suggested:' + suggestion.key) ? '' : null"
          [attr.aria-label]="'Add suggested tag #' + suggestion.key"
          matTooltip="Add recommended tag"
          (click)="acceptSuggestion(suggestion)"
        >
          <app-icon class="tag-suggestion-plus" name="sapphire-plus-circle" [size]="14" />
          <span class="tag-suggestion-name">#{{ suggestion.key }}</span>
        </button>
      }
    </div>

    @if (expanded()) {
      <button type="button" class="tags-item tags-more" (click)="showLess()">Less</button>
      @if (page() < pagesCount - 1) {
        <button type="button" class="tags-item tags-more" (click)="showMore()">More</button>
      }
    } @else if (hasOverflow) {
      <!-- On the second line, right after the last chip it leaves showing (Johanna, 2026-10-21). -->
    <button
      type="button"
      class="tags-item tags-more tags-more-inline"
      [class.placed]="!!morePosition"
      [style.left.px]="morePosition?.left"
      [style.top.px]="morePosition?.top"
      [style.height.px]="morePosition?.height"
      (click)="showMore()"
    >
      More
    </button>
    }
  </div>
</div>

<!-- [NAV-021] 'Manage my tags' form removed per task; uncomment (with the icon above) to re-enable
<ng-template #manageForm>
  <app-manage-tags-form
    [existingTags]="tags()"
    (close)="closePopovers()"
    (save)="saveTags($event)"
  />
</ng-template>
-->

<ng-template #addForm>
  <app-add-tags
    [stitchItems]="stitchItems"
    [tenantId]="owningTenantId"
    (close)="closePopovers()"
    (save)="saveTags($event)"
  />
</ng-template>
